import React, { useMemo } from 'react';
import {
  BarChart, Bar, PieChart, Pie, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell
} from 'recharts';
import type { FinanceRecord, CurrencyTotals } from '../../types/newFinance';
import { EXPENSE_CATEGORY_LABELS } from '../../types/newFinance';

interface Props {
  records: FinanceRecord[];
  selectedYear: number;
}

const COLORS = [
  '#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6',
  '#ec4899', '#06b6d4', '#84cc16', '#f97316', '#6366f1',
  '#14b8a6', '#f43f5e'
];

export const AnnualFinanceCharts: React.FC<Props> = ({ records, selectedYear }) => {
  // 월별 수입/지출 데이터
  const monthlyData = useMemo(() => {
    const data: Array<{
      month: string;
      수입: number;
      지출: number;
      순이익: number;
    }> = [];

    for (let month = 1; month <= 12; month++) {
      const monthRecords = records.filter(r => {
        const recordDate = new Date(r.date);
        return recordDate.getMonth() + 1 === month;
      });

      let income = 0;
      let expense = 0;

      monthRecords.forEach(record => {
        // HUF 기준으로 합산 (주요 통화)
        income += record.incomeTotals.HUF || 0;
        expense += record.expenseTotals.HUF || 0;
      });

      data.push({
        month: `${month}월`,
        수입: income,
        지출: expense,
        순이익: income - expense
      });
    }

    return data;
  }, [records]);

  // 카테고리별 지출 데이터
  const expenseCategoryData = useMemo(() => {
    const categoryTotals: { [key: string]: number } = {};

    records.forEach(record => {
      record.expenseItems.forEach(item => {
        const categoryLabel = EXPENSE_CATEGORY_LABELS[item.category];
        if (!categoryTotals[categoryLabel]) {
          categoryTotals[categoryLabel] = 0;
        }
        // HUF 기준
        if (item.currency === 'HUF') {
          categoryTotals[categoryLabel] += item.amount;
        }
      });
    });

    return Object.entries(categoryTotals)
      .map(([name, value]) => ({ name, value }))
      .filter(item => item.value > 0)
      .sort((a, b) => b.value - a.value);
  }, [records]);

  // 수입 종류별 데이터
  const incomeTypeData = useMemo(() => {
    let titheTotal = 0;
    let thanksgivingTotal = 0;
    let otherTotal = 0;

    records.forEach(record => {
      // 십일조
      record.titheItems.forEach(item => {
        if (item.currency === 'HUF') {
          titheTotal += item.amount;
        }
      });

      // 감사헌금
      record.thanksgivingItems.forEach(item => {
        if (item.currency === 'HUF') {
          thanksgivingTotal += item.amount;
        }
      });

      // 기타 헌금
      record.otherIncomeItems.forEach(item => {
        if (item.currency === 'HUF') {
          otherTotal += item.amount;
        }
      });
    });

    return [
      { name: '십일조', value: titheTotal },
      { name: '감사헌금', value: thanksgivingTotal },
      { name: '기타헌금', value: otherTotal }
    ].filter(item => item.value > 0);
  }, [records]);

  // 누적 트렌드 데이터
  const cumulativeTrendData = useMemo(() => {
    const data: Array<{
      month: string;
      누적수입: number;
      누적지출: number;
      누적순이익: number;
    }> = [];

    let cumulativeIncome = 0;
    let cumulativeExpense = 0;

    for (let month = 1; month <= 12; month++) {
      const monthRecords = records.filter(r => {
        const recordDate = new Date(r.date);
        return recordDate.getMonth() + 1 === month;
      });

      let monthIncome = 0;
      let monthExpense = 0;

      monthRecords.forEach(record => {
        monthIncome += record.incomeTotals.HUF || 0;
        monthExpense += record.expenseTotals.HUF || 0;
      });

      cumulativeIncome += monthIncome;
      cumulativeExpense += monthExpense;

      data.push({
        month: `${month}월`,
        누적수입: cumulativeIncome,
        누적지출: cumulativeExpense,
        누적순이익: cumulativeIncome - cumulativeExpense
      });
    }

    return data;
  }, [records]);

  // 커스텀 툴팁
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white dark:bg-gray-800 p-3 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 text-sm">
          <p className="font-bold text-gray-900 dark:text-white mb-2">{label}</p>
          {payload.map((entry: any, index: number) => (
            <p key={index} style={{ color: entry.color }} className="mb-1">
              {entry.name}: {entry.value.toLocaleString()} HUF
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  const PieTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0];
      return (
        <div className="bg-white dark:bg-gray-800 p-3 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 text-sm">
          <p className="font-bold text-gray-900 dark:text-white mb-1">{data.name}</p>
          <p className="text-gray-700 dark:text-gray-300">
            {data.value.toLocaleString()} HUF
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            {((data.value / data.payload.total) * 100).toFixed(1)}%
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="space-y-8 mt-6 animate-fadeIn">
      {/* 월별 수입/지출 막대 그래프 */}
      <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-200 dark:border-gray-700">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          📊 월별 수입/지출 현황
        </h3>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={monthlyData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.1} />
            <XAxis dataKey="month" stroke="#6b7280" fontSize={12} />
            <YAxis stroke="#6b7280" fontSize={12} />
            <Tooltip content={<CustomTooltip />} />
            <Legend wrapperStyle={{ fontSize: '12px' }} />
            <Bar dataKey="수입" fill="#10b981" radius={[4, 4, 0, 0]} barSize={20} />
            <Bar dataKey="지출" fill="#ef4444" radius={[4, 4, 0, 0]} barSize={20} />
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* 수입 종류별 원형 차트 */}
        {incomeTypeData.length > 0 && (
          <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              💰 수입 종류별 비율
            </h3>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={incomeTypeData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {incomeTypeData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip content={<PieTooltip />} />
                <Legend wrapperStyle={{ fontSize: '12px' }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* 지출 카테고리별 원형 차트 */}
        {expenseCategoryData.length > 0 && (
          <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              💸 지출 항목별 비율
            </h3>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={expenseCategoryData.slice(0, 8)}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {expenseCategoryData.slice(0, 8).map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip content={<PieTooltip />} />
                <Legend wrapperStyle={{ fontSize: '12px' }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      {/* 연간 누적 트렌드 라인 차트 */}
      <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-200 dark:border-gray-700">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          📈 연간 누적 트렌드
        </h3>
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={cumulativeTrendData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.1} />
            <XAxis dataKey="month" stroke="#6b7280" fontSize={12} />
            <YAxis stroke="#6b7280" fontSize={12} />
            <Tooltip content={<CustomTooltip />} />
            <Legend wrapperStyle={{ fontSize: '12px' }} />
            <Line type="monotone" dataKey="누적수입" stroke="#10b981" strokeWidth={2} activeDot={{ r: 8 }} />
            <Line type="monotone" dataKey="누적지출" stroke="#ef4444" strokeWidth={2} activeDot={{ r: 8 }} />
            <Line type="monotone" dataKey="누적순이익" stroke="#3b82f6" strokeWidth={2} activeDot={{ r: 8 }} />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};
